/**
 * AI advisor + automation engine (mock).
 *
 * `analyzeSecurity` derives prioritized, human-readable recommendations from the
 * current settings — the same job a server-side LLM/agent would do. Swap the
 * body for a Firebase callable that forwards state to the model and returns the
 * same `SecurityInsight[]` shape:
 *
 *   const call = httpsCallable(functions, `${FN_PREFIX}securityAdvisor`);
 *   return (await call({ security, notifications, location })).data;
 *
 * `getAutomationRules` / `evaluateAutomations` back the "Smart Automation"
 * surface: user-toggleable rules plus AI-managed rules that react to context.
 */
import type {
  SettingsState,
  SecurityInsight,
  AutomationRule,
} from '../../types/settings.types';

const RULES_KEY = 'messego.settings.automations.v1';

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

export const aiService = {
  /** Analyze the account and return prioritized security recommendations. */
  async analyzeSecurity(state: SettingsState): Promise<SecurityInsight[]> {
    const { security, notifications, location } = state;
    const insights: SecurityInsight[] = [];

    if (!security.twoFactorEnabled) {
      insights.push({
        id: 'ai-2fa',
        title: 'Enable two-factor authentication',
        detail: 'Accounts with 2FA are 99.9% less likely to be compromised. Add an authenticator app for a strong second layer.',
        severity: 'critical',
        actionLabel: 'Set up 2FA',
        actionHref: '/app/settings/security/two-factor',
      });
    }

    const pwAge = daysSince(security.lastPasswordChange);
    if (pwAge > 90) {
      insights.push({
        id: 'ai-pw-age',
        title: 'Your password is getting old',
        detail: `It has been ${pwAge} days since your last change. Rotating it periodically limits exposure from breaches on other sites.`,
        severity: 'warning',
        actionLabel: 'Change password',
        actionHref: '/app/settings/security/change-password',
      });
    }

    if (!security.appLockEnabled) {
      insights.push({
        id: 'ai-app-lock',
        title: 'Turn on App Lock',
        detail: 'Require a passcode or biometrics when returning to Messego so a borrowed or lost device stays private.',
        severity: 'info',
        actionLabel: 'Enable App Lock',
        actionHref: '/app/settings/security/app-lock',
      });
    }

    if (!notifications.categories.security.push && !notifications.categories.security.email) {
      insights.push({
        id: 'ai-sec-alerts',
        title: 'Security alerts are muted',
        detail: 'You will not be warned about new sign-ins or password changes. Keep at least one security channel on.',
        severity: 'warning',
        actionLabel: 'Review notifications',
        actionHref: '/app/settings/notifications',
      });
    }

    const otherSessions = security.activeSessions.filter((s) => !s.current);
    if (otherSessions.length >= 2) {
      insights.push({
        id: 'ai-sessions',
        title: `${otherSessions.length} other devices are signed in`,
        detail: 'Review your active sessions and sign out anything you do not recognize.',
        severity: 'info',
        actionLabel: 'Review sessions',
        actionHref: '/app/settings/security',
      });
    }

    if (location.precision === 'precise' && location.shareWithBusinesses) {
      insights.push({
        id: 'ai-location',
        title: 'You are sharing precise location with businesses',
        detail: 'Consider switching to approximate location to reduce how much of your movement is exposed.',
        severity: 'info',
        actionLabel: 'Adjust location',
        actionHref: '/app/settings/location',
      });
    }

    if (insights.length === 0) {
      insights.push({
        id: 'ai-all-clear',
        title: 'Your account looks well protected',
        detail: 'No high-priority issues detected. The advisor re-checks every time you open Security.',
        severity: 'success',
      });
    }

    // Simulate model latency.
    return new Promise((resolve) => setTimeout(() => resolve(insights), 500));
  },

  /** Compute an overall 0–100 security score from the current settings. */
  securityScore(state: SettingsState): number {
    const { security, notifications } = state;
    let score = 40;
    if (security.twoFactorEnabled) score += 25;
    if (security.appLockEnabled) score += 10;
    if (security.biometricEnabled) score += 5;
    if (security.loginAlerts) score += 5;
    if (daysSince(security.lastPasswordChange) <= 90) score += 10;
    if (notifications.categories.security.push || notifications.categories.security.email) score += 5;
    return Math.min(100, score);
  },

  getAutomationRules(): AutomationRule[] {
    const defaults: AutomationRule[] = [
      {
        id: 'auto-quiet',
        name: 'Smart Quiet Hours',
        description: 'Automatically silences non-urgent notifications overnight based on your usage patterns.',
        category: 'notifications',
        enabled: true,
        trigger: 'Between 10:00 PM and 7:00 AM',
        action: 'Mute social & marketing alerts',
        aiManaged: true,
      },
      {
        id: 'auto-focus',
        name: 'Focus Detection',
        description: 'Detects deep-work sessions and batches low-priority pings until you take a break.',
        category: 'notifications',
        enabled: false,
        trigger: 'Continuous activity for 25+ min',
        action: 'Batch non-critical notifications',
        aiManaged: true,
      },
      {
        id: 'auto-datasaver',
        name: 'Adaptive Data Saver',
        description: 'Enables Data Saver automatically when you leave Wi-Fi or approach your data limit.',
        category: 'data',
        enabled: true,
        trigger: 'On cellular & usage > 80%',
        action: 'Enable Data Saver + Wi-Fi media only',
        aiManaged: true,
      },
      {
        id: 'auto-theme',
        name: 'Auto Dark Mode',
        description: 'Switches to dark theme after sunset and back to light in the morning.',
        category: 'appearance',
        enabled: false,
        trigger: 'At local sunset / sunrise',
        action: 'Toggle theme',
        aiManaged: false,
      },
      {
        id: 'auto-lock',
        name: 'Auto App Lock',
        description: 'Locks Messego automatically when a new, unrecognized network is detected.',
        category: 'security',
        enabled: false,
        trigger: 'Unknown network joined',
        action: 'Require re-authentication',
        aiManaged: true,
      },
    ];
    try {
      const raw = localStorage.getItem(RULES_KEY);
      if (!raw) return defaults;
      const overrides = JSON.parse(raw) as Record<string, boolean>;
      return defaults.map((r) => (r.id in overrides ? { ...r, enabled: overrides[r.id] } : r));
    } catch {
      return defaults;
    }
  },

  async setAutomationEnabled(id: string, enabled: boolean): Promise<void> {
    try {
      const raw = localStorage.getItem(RULES_KEY);
      const overrides = raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
      overrides[id] = enabled;
      localStorage.setItem(RULES_KEY, JSON.stringify(overrides));
    } catch {
      /* non-fatal */
    }
    return new Promise((resolve) => setTimeout(resolve, 200));
  },
};
